/** @typedef {{ type: 'field', fieldId: string }} FieldAnchor */
/** @typedef {{ type: 'section', sectionId: string }} SectionAnchor */
/** @typedef {{ type: 'tableCell', table: 'raci' | 'resources', rowIndex: number, colIndex: number }} CellAnchor */
/** @typedef {{ type: 'textQuote', baseKey: string, quote: string }} TextQuoteAnchor */

export function stableAnchorKey(anchor) {
  if (!anchor || typeof anchor !== "object") return "";
  if (anchor.type === "field") return `field:${anchor.fieldId}`;
  if (anchor.type === "section") return `section:${anchor.sectionId}`;
  if (anchor.type === "tableCell") return `cell:${anchor.table}:${anchor.rowIndex}:${anchor.colIndex}`;
  if (anchor.type === "textQuote") {
    const q = String(anchor.quote || "").slice(0, 120);
    return `textQuote:${anchor.baseKey}:${q}`;
  }
  return JSON.stringify(anchor);
}

const LABELS = {
  gapReport: "Gap Analysis Report",
  "exhibit.effectiveDate": "Exhibit A - Effective date",
  "exhibit.scopeOfWork": "Exhibit A - Scope of work",
  "exhibit.projectPlan": "Exhibit A - Project plan",
  "exhibit.priceEstimate": "Exhibit A - Price estimate",
  "exhibit.totalPayment": "Exhibit A - Total payment",
  "exhibit.paymentTerms": "Exhibit A - Payment tranches",
  "clause.duration": "Clause 3 - Duration",
  "section.docHeader": "Document title & parties",
  "section.introBody": "Intro & party blocks",
  "section.clauses": "Standard clauses",
  "section.witness": "Witness / signatures",
  "section.exhibitHeader": "Exhibit A header",
  "section.exhibit": "Exhibit A",
  "section.exhibitGrid": "Exhibit A - details",
  "section.paymentBlock": "Payment terms",
  "section.finalSignatures": "Final signatures",
  "document.root": "Document",
};

export function anchorDisplayLabel(anchor) {
  if (!anchor || typeof anchor !== "object") return "Contract";
  if (anchor.type === "textQuote") {
    const base = LABELS[anchor.baseKey] || anchor.baseKey || "Section";
    return `${base} - selected text`;
  }
  if (anchor.type === "field") return LABELS[anchor.fieldId] || anchor.fieldId;
  if (anchor.type === "section") {
    const sid = anchor.sectionId;
    if (typeof sid === "string" && sid.startsWith("append.")) return "Additional contract section";
    return LABELS[sid] || sid;
  }
  if (anchor.type === "tableCell") {
    const t = anchor.table === "raci" ? "RACI" : "Resources";
    return `${t} - row ${(anchor.rowIndex ?? 0) + 1}, col ${(anchor.colIndex ?? 0) + 1}`;
  }
  return "Contract";
}

export function newId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `ca-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

export function createContractAnnotation({ authorRole, body, anchor, kind = "comment" }) {
  const k = kind === "suggestion" ? "suggestion" : kind === "reaction" ? "reaction" : "comment";
  return {
    id: newId(),
    authorRole,
    createdAt: Date.now(),
    body: (body || "").trim(),
    status: "open",
    anchor,
    kind: k,
    replies: [],
    reactions: [],
  };
}

export function normalizeContractAnnotations(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.filter((a) => a && typeof a === "object" && typeof a.id === "string" && a.anchor && typeof a.anchor === "object");
}

export function countOpenForAnchorKey(list, anchorKey) {
  return list.filter((a) => a.status !== "resolved" && stableAnchorKey(a.anchor) === anchorKey).length;
}

export function addReplyToAnnotation(list, annotationId, reply) {
  const body = (reply.body || "").trim();
  if (!body) return list;
  return list.map((a) => {
    if (a.id !== annotationId) return a;
    const replies = Array.isArray(a.replies) ? [...a.replies] : [];
    replies.push({ authorRole: reply.authorRole, body, createdAt: Date.now() });
    return { ...a, replies };
  });
}

export function resolveAnnotation(list, annotationId, resolvedByRole) {
  return list.map((a) =>
    a.id === annotationId ? { ...a, status: "resolved", resolvedAt: Date.now(), resolvedBy: resolvedByRole } : a
  );
}

export function addReactionToAnnotation(list, annotationId, emoji, authorRole) {
  return list.map((a) => {
    if (a.id !== annotationId) return a;
    const reactions = Array.isArray(a.reactions) ? [...a.reactions] : [];
    reactions.push({ emoji, authorRole, createdAt: Date.now() });
    return { ...a, reactions };
  });
}

/** Walk up from a DOM node to find data-contract-anchor value */
export function findNearestContractAnchorKey(startNode) {
  let el = startNode && startNode.nodeType === 1 ? startNode : startNode?.parentElement;
  while (el) {
    const v = el.getAttribute?.("data-contract-anchor");
    if (v) return v;
    el = el.parentElement;
  }
  return "document.root";
}

export function parseContractAnchorKey(key) {
  if (!key) return { type: "section", sectionId: "document.root" };
  const cellRaci = /^cell:raci:(\d+):(\d+)$/.exec(key);
  if (cellRaci) return { type: "tableCell", table: "raci", rowIndex: Number(cellRaci[1]), colIndex: Number(cellRaci[2]) };
  const cellRes = /^cell:resources:(\d+):(\d+)$/.exec(key);
  if (cellRes) return { type: "tableCell", table: "resources", rowIndex: Number(cellRes[1]), colIndex: Number(cellRes[2]) };
  return { type: "section", sectionId: key };
}

/** Build persisted anchor: selection comments use textQuote + optional table/section base */
export function anchorFromSelection(baseDomKey, selectedText) {
  const trimmed = (selectedText || "").trim();
  const base = parseContractAnchorKey(baseDomKey);
  if (trimmed.length < 1) return base;
  const quote = trimmed.length > 600 ? trimmed.slice(0, 600) : trimmed;
  const baseKey = baseDomKey || "document.root";
  return { type: "textQuote", baseKey, quote };
}
